import { useEffect, useState } from "react";
import { Loader2, Plus, X, Sun, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const emptyItem = () => ({ id: Math.random().toString(36).slice(2, 12), name: "", description: "", price: "" });

export default function DailyMenu() {
  const [menu, setMenu] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/daily-menu");
        setMenu({ ...data, items: data.items.length ? data.items : [emptyItem()] });
      } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    })();
  }, []);

  const setItem = (id, k, v) => setMenu((m) => ({ ...m, items: m.items.map((it) => (it.id === id ? { ...it, [k]: v } : it)) }));
  const addItem = () => setMenu((m) => ({ ...m, items: [...m.items, emptyItem()] }));
  const rmItem = (id) => setMenu((m) => ({ ...m, items: m.items.filter((it) => it.id !== id) }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/daily-menu", {
        note: menu.note,
        items: menu.items.filter((it) => it.name.trim()).map((it) => ({ ...it, price: Number(String(it.price).replace(",", ".")) || 0 })),
      });
      setMenu({ ...data, items: data.items.length ? data.items : [emptyItem()] });
      toast.success(data.items.length ? "Menù del giorno pubblicato!" : "Menù del giorno svuotato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const clearAll = async () => {
    setMenu((m) => ({ ...m, note: "", items: [emptyItem()] }));
    try { await api.put("/daily-menu", { note: "", items: [] }); toast.success("Menù del giorno svuotato"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (error) return (
    <div className="max-w-lg rounded-2xl border border-amber-200 bg-amber-50/60 p-6" data-testid="daily-locked">
      <h1 className="font-display font-bold text-3xl mb-2">Menù del giorno</h1>
      <p className="text-gray-600">{error}</p>
    </div>
  );
  if (!menu) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#5aa469]" /></div>;

  const published = menu.items.filter((it) => it.name.trim()).length;
  const dateLabel = new Date(menu.date + "T12:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });

  return (
    <div className="space-y-6 max-w-2xl" data-testid="daily-menu-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display font-bold text-4xl">Menù del giorno</h1>
          <p className="text-gray-500 mt-1 capitalize">{dateLabel}</p>
        </div>
        <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium ${published ? "bg-[#5aa469]/12 text-[#4a8a56]" : "bg-gray-100 text-gray-500"}`} data-testid="daily-status">
          <Sun className="h-4 w-4" /> {published ? `${published} ${published === 1 ? "piatto" : "piatti"} in evidenza oggi` : "Nessun piatto oggi"}
        </span>
      </div>

      <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4 text-sm text-amber-800">
        I piatti che inserisci qui appaiono in cima al menù pubblico con il badge <strong>Oggi</strong> e si azzerano automaticamente a mezzanotte.
      </div>

      <div className="rounded-2xl mo-card p-6 space-y-5">
        <div>
          <Label>Nota (facoltativa)</Label>
          <Textarea value={menu.note} onChange={(e) => setMenu((m) => ({ ...m, note: e.target.value }))} placeholder="Es. Oggi pesce fresco dal mercato — fino a esaurimento" className="mt-1.5 rounded-lg focus:border-[#5aa469]" data-testid="daily-note" />
        </div>
        <div className="space-y-3">
          <Label>Piatti di oggi</Label>
          {menu.items.map((it, i) => (
            <div key={it.id} className="rounded-xl border border-gray-200 p-3 space-y-2" data-testid={`daily-item-${i}`}>
              <div className="flex gap-2">
                <Input value={it.name} onChange={(e) => setItem(it.id, "name", e.target.value)} placeholder="Nome piatto" className="h-10 rounded-lg" data-testid={`daily-item-name-${i}`} />
                <Input type="number" step="0.01" min="0" value={it.price} onChange={(e) => setItem(it.id, "price", e.target.value)} placeholder="€" className="h-10 w-28 rounded-lg" data-testid={`daily-item-price-${i}`} />
                <Button type="button" variant="ghost" size="icon" onClick={() => rmItem(it.id)} className="text-red-500 hover:bg-gray-100 shrink-0" data-testid={`daily-item-remove-${i}`}><X className="h-4 w-4" /></Button>
              </div>
              <Input value={it.description} onChange={(e) => setItem(it.id, "description", e.target.value)} placeholder="Descrizione breve (facoltativa)" className="h-9 rounded-lg text-sm" data-testid={`daily-item-desc-${i}`} />
            </div>
          ))}
          <Button type="button" variant="ghost" onClick={addItem} className="text-[#5aa469] hover:bg-[#5aa469]/10" data-testid="daily-add-item"><Plus className="h-4 w-4 mr-1" /> Aggiungi piatto</Button>
        </div>
        <div className="flex gap-3 flex-wrap">
          <Button onClick={save} disabled={saving} className="rounded-full bg-[#5aa469] hover:bg-[#4a8a56] text-white font-semibold active:scale-95 transition-transform" data-testid="daily-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" />Pubblica menù di oggi</>}
          </Button>
          <Button variant="outline" onClick={clearAll} className="rounded-full border-gray-300" data-testid="daily-clear">Svuota</Button>
        </div>
      </div>
    </div>
  );
}
